import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { ExchangeRate } from "../../../types/admin";

interface ExchangeRateModalProps {
  open: boolean;
  mode: "create" | "edit";
  initialData?: ExchangeRate;
  onClose: () => void;
  onSave: (payload: {
    sellValue: number;
    buyValue: number;
    isActive: boolean;
    comment: string;
  }) => Promise<void> | void;
  submitting?: boolean;
}

const ExchangeRateModal: React.FC<ExchangeRateModalProps> = ({
  open,
  mode,
  initialData,
  onClose,
  onSave,
  submitting,
}) => {
  const { t } = useTranslation();
  const [sellValue, setSellValue] = useState<number>(
    initialData?.sellValue ?? 9.3
  );
  const [buyValue, setBuyValue] = useState<number>(
    initialData?.buyValue ?? 9.1
  );
  const [isActive, setIsActive] = useState<boolean>(
    initialData?.isActive ?? true
  );
  const [comment, setComment] = useState<string>(initialData?.comment ?? "");

  useEffect(() => {
    if (open) {
      setSellValue(initialData?.sellValue ?? 9.3);
      setBuyValue(initialData?.buyValue ?? 9.1);
      setIsActive(initialData?.isActive ?? true);
      setComment(initialData?.comment ?? "");
    }
  }, [open, initialData]);

  const handleSubmit = async () => {
    await onSave({
      sellValue: Number(sellValue) || 0,
      buyValue: Number(buyValue) || 0,
      isActive,
      comment: comment || "",
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {mode === "create" ? t("admin.createExchangeRate") : t("admin.editExchangeRate")}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 1 }}>
          <Stack spacing={2}>
            <TextField
              label={t("admin.sellValueUsdToTjs")}
              type="number"
              value={sellValue}
              onChange={(e) => setSellValue(Number(e.target.value) || 0)}
              inputProps={{ min: 0, step: 0.0001 }}
              fullWidth
              required
              helperText={t("admin.sellValueHelperText")}
            />
            <TextField
              label={t("admin.buyValueTjsToUsd")}
              type="number"
              value={buyValue}
              onChange={(e) => setBuyValue(Number(e.target.value) || 0)}
              inputProps={{ min: 0, step: 0.0001 }}
              fullWidth
              required
              helperText={t("admin.buyValueHelperText")}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              }
              label={t("common.active")}
            />
            <TextField
              label={t("common.comment")}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              fullWidth
              multiline
              rows={3}
              helperText={t("admin.exchangeRateCommentHelperText")}
            />
          </Stack>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit" disabled={submitting}>
          {t("common.cancel")}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={submitting}
        >
          {mode === "create" ? t("common.create") : t("common.save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExchangeRateModal;

